import { create } from 'zustand';
import { BrokerApi } from '@apply/api';
import { formatPhone } from '@apply/ui';

export type IAddress = BrokerApi.IAddress;

type IBrokerAdminResponse = BrokerApi.IBrokerAdminResponse;
type IBrokerResponse = BrokerApi.IBrokerResponse;
type IBrokerSettings = BrokerApi.IBrokerSettings;

// Used for change detection in admin mode
export enum FormField {
    // Business address
    BusinessStreet1 = 'businessAddress.street1',
    BusinessStreet2 = 'businessAddress.street2',
    BusinessCity = 'businessAddress.city',
    BusinessAddressState = 'businessAddress.state',
    BusinessZipCode = 'businessAddress.zipCode',
    // Contact info
    PhoneNumberA = 'phoneNumberA',
    PhoneNumberAType = 'phoneNumberAType',
    Email = 'email',
    SecondaryEmail = 'secondaryEmail',
    // Home address
    HomeStreet1 = 'homeAddress.street1',
    HomeStreet2 = 'homeAddress.street2',
    HomeCity = 'homeAddress.city',
    HomeAddressState = 'homeAddress.state',
    HomeZipCode = 'homeAddress.zipCode',
    // Personal
    DateOfBirth = 'dateOfBirth',
    FirstName = 'firstName',
    LastName = 'lastName',
    ArizonaLicenseNumber = 'arizonaLicenseNumber',
    // Banking
    WireBankName = 'wireBankName',
    WireRecipientName = 'wireRecipientName',
    WireAccountLastFourDigits = 'wireAccountLastFourDigits',
    // Company
    BusinessName = 'businessName',
    BusinessState = 'businessState',
    Title = 'title',
}

// Used for change detection in admin mode
export interface IReviewedSnapshot {
    businessAddress: Partial<IAddress>;
    homeAddress: Partial<IAddress>;
    phoneNumberA?: string;
    phoneNumberAType?: string;
    email?: string;
    secondaryEmail?: string;
    dateOfBirth?: Date;
    firstName?: string;
    lastName?: string;
    arizonaLicenseNumber?: string;
    wireBankName?: string;
    wireRecipientName?: string;
    wireAccountLastFourDigits?: string;
    businessName?: string;
    businessState?: string;
    title?: string;
}

export interface BrokerFormState {
    // User context
    isAdmin: boolean;
    isBroker: boolean;
    brokerId: number;

    // Addresses
    businessAddress: IAddress;
    homeAddress: IAddress;

    // Contact info
    phoneNumberA: string;
    phoneNumberAType: string;
    phoneNumberB: string;
    phoneNumberBType: string;
    email: string;
    secondaryEmail: string;

    // Personal
    dateOfBirth: Date | undefined;
    sameAsContactAddress: boolean;
    firstName: string;
    lastName: string;
    arizonaLicenseNumber: string;

    // Banking
    wireBankName: string;
    wireRecipientName: string;
    wireAccountLastFourDigits: string;

    // W9
    hasSubmittedW9: boolean;
    w9Url: string;
    w9FileName: string;
    w9File: File | undefined;

    // Company
    businessName: string;
    businessState: string;
    title: string;
    titleSelect: string;

    // Broker info
    id: number;
    brokerFlagged: boolean;
    iccaTemplateId: string;
    clientFeeType: string;
    pointOfContactId: string;
    backgroundCheckDate: Date | null;

    // ESign / consent
    canSendTextMessages: boolean;
    acceptedTos: boolean;
    eSignAgreement: boolean;
    eSignFont: string;

    // Status & lists
    brokerStatus: number;
    agreements: unknown[];
    pointsOfContact: unknown[];
    userAgreementHtml: string;

    // Company Logo File
    companyLogoUrl: string;
    companyLogoFile: File | undefined;

    // Review data
    reviewedSnapshot: IReviewedSnapshot | null;
}

const defaultAddress: IAddress = {
    street1: '',
    street2: '',
    city: '',
    state: '',
    zipCode: '',
};

const defaultState: BrokerFormState = {
    isAdmin: false,
    isBroker: false,
    brokerId: 0,
    businessAddress: { ...defaultAddress },
    homeAddress: { ...defaultAddress },
    phoneNumberA: '',
    phoneNumberAType: '',
    phoneNumberB: '',
    phoneNumberBType: '',
    email: '',
    secondaryEmail: '',
    dateOfBirth: undefined,
    sameAsContactAddress: false,
    firstName: '',
    lastName: '',
    arizonaLicenseNumber: '',
    wireBankName: '',
    wireRecipientName: '',
    wireAccountLastFourDigits: '',
    hasSubmittedW9: false,
    w9Url: '',
    w9FileName: '',
    w9File: undefined,
    businessName: '',
    businessState: '',
    title: '',
    titleSelect: '',
    id: 0,
    brokerFlagged: false,
    iccaTemplateId: '',
    clientFeeType: '',
    pointOfContactId: '',
    backgroundCheckDate: null,
    canSendTextMessages: false,
    acceptedTos: false,
    eSignAgreement: false,
    eSignFont: '',
    brokerStatus: -1,
    agreements: [],
    pointsOfContact: [],
    userAgreementHtml: '',
    companyLogoUrl: '',
    companyLogoFile: undefined,
    reviewedSnapshot: null,
};

interface BrokerDetailsStore extends BrokerFormState {
    // Set any combination of fields in one call
    updateFields: (partial: Partial<BrokerFormState>) => void;

    // Merge partial address updates into the nested address objects
    // (needed because updateFields({ businessAddress: { street1: 'x' } }) would wipe the other address fields)
    updateBusinessAddress: (partial: Partial<IAddress>) => void;
    updateHomeAddress: (partial: Partial<IAddress>) => void;

    // Populate the entire form from API data
    initializeFromBroker: (
        broker: IBrokerAdminResponse | IBrokerResponse | undefined,
        context: {
            isAdmin: boolean;
            isBroker: boolean;
            brokerId: number;
            brokerTitles: string[];
        }
    ) => void;

    // Populate the entire form from API data
    initializeFromBrokerSettings: (
        brokerSettings: IBrokerSettings | undefined,
        context: {
            isAdmin: boolean;
            isBroker: boolean;
            brokerId: number;
            brokerTitles: string[];
        }
    ) => void;

    // Restore defaults on unmount
    reset: () => void;
}

export const useBrokerDetailsStore = create<BrokerDetailsStore>((set) => ({
    ...defaultState,

    updateFields: (partial) => set(partial),

    updateBusinessAddress: (partial) => set((state) => ({ businessAddress: { ...state.businessAddress, ...partial } })),
    updateHomeAddress: (partial) => set((state) => ({ homeAddress: { ...state.homeAddress, ...partial } })),

    initializeFromBroker: (broker, context) => {
        const { isAdmin, isBroker, brokerId, brokerTitles } = context;
        const brokerStatus = broker?.brokerStatus ?? -1;
        const adminBroker = broker && 'iccaTemplateId' in broker ? (broker as IBrokerAdminResponse) : undefined;

        let titleSelect = '';

        if (broker?.businessName) {
            if (broker?.title && brokerTitles.indexOf(broker?.title) === -1) {
                titleSelect = 'Custom';
            } else if (broker?.title) {
                titleSelect = broker.title;
            }
        }

        // Parse review snapshot
        const lastEditJson = adminBroker?.lastEditJson ?? null;
        let reviewedSnapshot: IReviewedSnapshot | null = null;

        if (lastEditJson) {
            const json = JSON.parse(lastEditJson);
            reviewedSnapshot = {
                businessAddress: {
                    street1: json.streetAddress,
                    street2: json.streetAddress2,
                    city: json.city,
                    state: json.state,
                    zipCode: json.zipCode,
                },
                homeAddress: {
                    street1: json.homeStreetAddress,
                    street2: json.homeStreetAddress2,
                    city: json.homeCity,
                    state: json.homeState,
                    zipCode: json.homeZipCode,
                },
                phoneNumberA: json.phoneNumberA,
                phoneNumberAType: json.phoneNumberAType,
                email: json.email,
                secondaryEmail: json.secondaryEmail,
                dateOfBirth: json.dateOfBirth ? new Date(Date.parse(json.dateOfBirth)) : undefined,
                firstName: json.firstName,
                lastName: json.lastName,
                arizonaLicenseNumber: json.arizonaLicenseNumber,
                wireBankName: json.wireBankName,
                wireRecipientName: json.wireRecipientName,
                wireAccountLastFourDigits: json.wireAccountLastFourDigits,
                businessName: json.businessName,
                businessState: json.businessState,
                title: json.title,
            };
        }

        // Compute sameAsContactAddress
        const sameAsContactAddress = !!(
            broker?.businessAddress?.street1 &&
            broker?.businessAddress?.city &&
            broker?.businessAddress?.street1 === broker?.homeAddress?.street1 &&
            broker?.businessAddress?.street2 === broker?.homeAddress?.street2 &&
            broker?.businessAddress?.city === broker?.homeAddress?.city &&
            broker?.businessAddress?.state === broker?.homeAddress?.state &&
            broker?.businessAddress?.zipCode === broker?.homeAddress?.zipCode
        );

        set({
            isAdmin,
            isBroker,
            brokerId,
            id: broker?.id || 0,
            firstName: broker?.firstName ?? '',
            lastName: broker?.lastName ?? '',
            arizonaLicenseNumber: broker?.arizonaLicenseNumber ?? '',
            wireBankName: broker?.wireBankName ?? '',
            wireRecipientName: broker?.wireRecipientName ?? '',
            wireAccountLastFourDigits: broker?.wireAccountLastFourDigits ?? '',
            hasSubmittedW9: broker?.hasSubmittedW9 ?? false,
            w9Url: adminBroker?.w9Url ?? '',
            w9FileName: broker?.w9FileName ?? '',
            w9File: undefined,
            businessName: broker?.businessName ?? '',
            businessState: broker?.businessState ?? '',
            title: broker?.title ?? '',
            titleSelect,
            brokerFlagged: broker?.isFlagged ?? false,
            iccaTemplateId: adminBroker?.iccaTemplateId != null ? String(adminBroker.iccaTemplateId) : '',
            clientFeeType: adminBroker?.clientFeeType ?? '',
            pointOfContactId: broker?.pointOfContactId ?? '',
            backgroundCheckDate: adminBroker?.backgroundCheckDate ? new Date(adminBroker.backgroundCheckDate) : null,
            canSendTextMessages: broker?.canSendTextMessages ?? false,
            acceptedTos: broker?.acceptedTos ?? false,
            eSignAgreement: broker?.acceptedTos ?? false,
            eSignFont: '',
            brokerStatus,
            companyLogoUrl: '',
            companyLogoFile: undefined,
            reviewedSnapshot,

            // Contact address
            businessAddress: {
                street1: broker?.businessAddress?.street1 ?? '',
                street2: broker?.businessAddress?.street2 ?? '',
                city: broker?.businessAddress?.city ?? '',
                state: broker?.businessAddress?.state ?? '',
                zipCode: broker?.businessAddress?.zipCode ?? '',
            },
            phoneNumberA: formatPhone(broker?.phoneNumberA ?? '') ?? '',
            phoneNumberAType: broker?.phoneNumberAType ?? '',
            phoneNumberB: formatPhone(broker?.phoneNumberB ?? '') ?? '',
            phoneNumberBType: broker?.phoneNumberBType ?? '',
            email: broker?.email ?? '',
            secondaryEmail: broker?.secondaryEmail ?? '',

            // Home address
            homeAddress: {
                street1: broker?.homeAddress?.street1 ?? '',
                street2: broker?.homeAddress?.street2 ?? '',
                city: broker?.homeAddress?.city ?? '',
                state: broker?.homeAddress?.state ?? '',
                zipCode: broker?.homeAddress?.zipCode ?? '',
            },
            sameAsContactAddress,

            // Date of birth
            dateOfBirth: broker?.dateOfBirth ? new Date(broker.dateOfBirth) : undefined,
        });
    },

    initializeFromBrokerSettings: (brokerSettings, context) => {
        const { isAdmin, isBroker, brokerId, brokerTitles } = context;
        let titleSelect = '';

        if (brokerSettings?.businessName) {
            if (brokerSettings?.title && brokerTitles.indexOf(brokerSettings?.title) === -1) {
                titleSelect = 'Custom';
            } else if (brokerSettings?.title) {
                titleSelect = brokerSettings.title;
            }
        }

        // Compute sameAsContactAddress
        const sameAsContactAddress = !!(
            brokerSettings?.businessAddress?.street1 &&
            brokerSettings?.businessAddress?.city &&
            brokerSettings?.businessAddress?.street1 === brokerSettings?.homeAddress?.street1 &&
            brokerSettings?.businessAddress?.street2 === brokerSettings?.homeAddress?.street2 &&
            brokerSettings?.businessAddress?.city === brokerSettings?.homeAddress?.city &&
            brokerSettings?.businessAddress?.state === brokerSettings?.homeAddress?.state &&
            brokerSettings?.businessAddress?.zipCode === brokerSettings?.homeAddress?.zipCode
        );

        set({
            isAdmin,
            isBroker,
            brokerId,
            firstName: brokerSettings?.firstName ?? '',
            lastName: brokerSettings?.lastName ?? '',
            arizonaLicenseNumber: brokerSettings?.arizonaLicenseNumber ?? '',
            wireBankName: brokerSettings?.wireBankName ?? '',
            wireRecipientName: brokerSettings?.wireRecipientName ?? '',
            wireAccountLastFourDigits: brokerSettings?.wireAccountLastFourDigits ?? '',
            businessName: brokerSettings?.businessName ?? '',
            businessState: brokerSettings?.businessState ?? '',
            title: brokerSettings?.title ?? '',
            titleSelect,
            companyLogoUrl: brokerSettings?.companyLogoUrl,
            companyLogoFile: undefined,

            // Contact address
            businessAddress: {
                street1: brokerSettings?.businessAddress?.street1 ?? '',
                street2: brokerSettings?.businessAddress?.street2 ?? '',
                city: brokerSettings?.businessAddress?.city ?? '',
                state: brokerSettings?.businessAddress?.state ?? '',
                zipCode: brokerSettings?.businessAddress?.zipCode ?? '',
            },
            phoneNumberA: formatPhone(brokerSettings?.phoneNumberA ?? '') ?? '',
            phoneNumberAType: brokerSettings?.phoneNumberAType ?? '',
            phoneNumberB: formatPhone(brokerSettings?.phoneNumberB ?? '') ?? '',
            phoneNumberBType: brokerSettings?.phoneNumberBType ?? '',
            email: brokerSettings?.emailAddressA ?? '',
            secondaryEmail: brokerSettings?.secondaryEmail ?? '',

            // Home address
            homeAddress: {
                street1: brokerSettings?.homeAddress?.street1 ?? '',
                street2: brokerSettings?.homeAddress?.street2 ?? '',
                city: brokerSettings?.homeAddress?.city ?? '',
                state: brokerSettings?.homeAddress?.state ?? '',
                zipCode: brokerSettings?.homeAddress?.zipCode ?? '',
            },
            sameAsContactAddress,

            // Date of birth
            dateOfBirth: brokerSettings?.dateOfBirth ? new Date(brokerSettings.dateOfBirth) : undefined,
        });
    },

    reset: () => set({ ...defaultState, businessAddress: { ...defaultAddress }, homeAddress: { ...defaultAddress } }),
}));
